import { ResourceDefinition } from './types';
import { ResolverHttpClient } from '../resolvers/definition/httpClient';
import { PromiseMode } from '../client/types';

export const skipToken: unique symbol = Symbol.for('pulse.skipToken');

export type SkipToken = typeof skipToken;

export const isSkipToken = (value: unknown): value is SkipToken =>
  value === skipToken;

export type BaseOptions = {
  skip?: boolean;
  pollInterval?: number;
  httpClient?: ResolverHttpClient;
  pollGroup?: string;
  passive?: boolean;
  keepAliveWhenHidden?: boolean;
};

export const emptyInitialParams = Object.freeze({});

export const normalizeResourceOptions = (
  options?: BaseOptions & {
    params?: any;
  }
) => {
  const skipTokenPassed = isSkipToken(options?.params);

  const params = skipTokenPassed
    ? (emptyInitialParams as any)
    : options?.params ?? (emptyInitialParams as any);

  const skip = !!options?.skip || skipTokenPassed;

  return {
    params,
    skip,
    pollInterval: options?.pollInterval,
    pollGroup: options?.pollGroup,
    httpClient: options?.httpClient,
    passive: options?.passive,
    keepAliveWhenHidden: options?.keepAliveWhenHidden,
  };
};

export type BaseDescriptor<TParams, TData, TError extends Error = Error> = {
  resource: ResourceDefinition<TParams, TData, TError>;
  params?: TParams;
  skip: boolean;
  pollInterval?: number;
  httpClient?: ResolverHttpClient;
  pollGroup?: string;
  passive?: boolean;
  keepAliveWhenHidden?: boolean;
  select?: (data: any) => unknown;
};

export type BaseDescriptorWithKey<
  TParams,
  TData,
  TError extends Error = Error,
> = BaseDescriptor<TParams, TData, TError> & {
  instanceKey: string;
};

export type ResourceWatchDescriptor<
  TParams,
  TData,
  TError extends Error = Error,
> = BaseDescriptor<TParams, TData, TError> & {
  __errorType?: TError;
};

export type ResourceLoadDescriptor<
  TMode extends PromiseMode,
  TParams,
  TData,
  TError extends Error = Error,
> = BaseDescriptor<TParams, TData, TError> & {
  mode?: TMode;
};
